import { useCallback, useRef, useEffect, useState } from 'react';
import { Howl } from 'howler';
import { SoundEffect } from '../types';
import { useFilterStore } from '../store/filterStore';

export const useSound = () => {
  const soundEnabled = useFilterStore((state) => state.soundEnabled);
  const soundsRef = useRef<Howl | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // Check if user prefers reduced motion - disable sound by default if true
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      console.info('Sound effects disabled due to prefers-reduced-motion setting');
      return;
    }

    // Initialize sound sprites with error handling
    soundsRef.current = new Howl({
      src: ['/audio/ui-sounds.mp3', '/audio/ui-sounds.webm'], // Multiple formats for compatibility
      volume: 0.4,
      preload: true,
      sprite: {
        cardFlow: [0, 800],
        filterClick: [1000, 200],
        cardExclude: [1500, 400],
        cardInclude: [2000, 300],
        clusterSnap: [2500, 250],
        complete: [3000, 1200],
        tabSwitch: [4500, 300],
        reveal: [5000, 900],
      },
      onload: () => {
        setIsLoaded(true);
        console.info('Sound effects loaded successfully');
      },
      onloaderror: (id, error) => {
        setHasError(true);
        console.warn('Sound loading failed - continuing without audio:', error);
        console.info('To enable sound effects, add audio files to public/audio/');
        console.info('See public/audio/README.md for instructions');
        // Graceful degradation - app continues without sound
      },
      onplayerror: (id, error) => {
        console.warn('Sound playback error:', error);
        // Attempt to unlock audio on user interaction
        soundsRef.current?.once('unlock', () => {
          console.info('Audio unlocked - retrying playback');
        });
      },
    });

    return () => {
      soundsRef.current?.unload();
    };
  }, []);

  const play = useCallback(
    (effect: SoundEffect) => {
      // Only play if:
      // - Sound is enabled in settings
      // - Audio is loaded successfully
      // - No loading errors occurred
      // - Howl instance exists
      if (soundEnabled && isLoaded && !hasError && soundsRef.current) {
        try {
          soundsRef.current.play(effect);
        } catch (error) {
          console.warn(`Failed to play sound effect "${effect}":`, error);
        }
      }
    },
    [soundEnabled, isLoaded, hasError]
  );

  return {
    play,
    isLoaded,
    hasError,
    isAvailable: isLoaded && !hasError,
  };
};
